import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, Plus, Trash2, CheckCircle2, Camera } from "lucide-react";
import { PhotoField } from "@/components/CarnetRoute";
import { ClickablePhoto } from "@/components/PhotoLightbox";

const GRAVITES = [
  { value: "mineur", label: "Mineur", badge: "bg-slate-100 text-slate-600" },
  { value: "moyen", label: "Moyen", badge: "bg-amber-100 text-amber-700" },
  { value: "critique", label: "Critique", badge: "bg-red-100 text-red-700" },
];

export const ProblemesVehicule = ({ vehicule, onChange }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const problemes = [...(vehicule.problemes || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const ouverts = problemes.filter(p => p.statut !== "resolu");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(null); // probleme object
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [resolveForm, setResolveForm] = useState({ resolution_note: "", cout: "" });

  const openNew = () => {
    setForm({ titre: "", description: "", gravite: "moyen", signale_par: "", photo: "", km: vehicule.kilometrage || "" });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.titre?.trim()) { toast.error("Titre obligatoire"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/vehicules/${vehicule.id}/problemes`, { ...form, km: form.km ? parseInt(form.km) : null }, { headers });
      toast.success("Problème signalé");
      setDialogOpen(false);
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const resolve = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vehicules/${vehicule.id}/problemes/${resolveOpen.id}`, {
        statut: "resolu",
        resolution_note: resolveForm.resolution_note,
        cout: resolveForm.cout ? parseFloat(resolveForm.cout) : null,
      }, { headers });
      toast.success("Problème résolu");
      setResolveOpen(null);
      onChange();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Supprimer ce signalement ?")) return;
    try { await axios.delete(`${API}/vehicules/${vehicule.id}/problemes/${pid}`, { headers }); toast.success("Supprimé"); onChange(); }
    catch { toast.error("Erreur"); }
  };

  const gravInfo = (g) => GRAVITES.find(x => x.value === g) || GRAVITES[1];

  return (
    <Card data-testid="problemes-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          Problèmes signalés {ouverts.length > 0 && <Badge className="bg-red-500">{ouverts.length} ouvert{ouverts.length > 1 ? "s" : ""}</Badge>}
        </CardTitle>
        <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={openNew} data-testid="new-probleme-btn"><Plus className="w-4 h-4 mr-1" />Signaler</Button>
      </CardHeader>
      <CardContent>
        {problemes.length === 0 ? (
          <p className="text-slate-400 text-sm">Aucun problème signalé. 👍</p>
        ) : (
          <div className="space-y-2">
            {problemes.map(p => {
              const g = gravInfo(p.gravite);
              const resolu = p.statut === "resolu";
              return (
                <div key={p.id} className={`p-3 rounded-lg border ${resolu ? "bg-slate-50 opacity-70" : "bg-white"}`} data-testid={`probleme-row-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium ${resolu ? "line-through text-slate-500" : "text-slate-800"}`}>{p.titre}</span>
                        <Badge className={`text-xs ${g.badge}`} variant="outline">{g.label}</Badge>
                        {resolu ? <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">✅ Résolu</Badge> : <Badge className="bg-red-500 text-xs">Ouvert</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                        {p.signale_par && <span>Par {p.signale_par}</span>}
                        {p.date && <span>{p.date.slice(0, 10)}</span>}
                        {p.km != null && p.km !== "" && <span>{Number(p.km).toLocaleString()} km</span>}
                        {p.cout != null && <span className="text-indigo-600 font-medium">{Number(p.cout).toFixed(2)} € réparation</span>}
                      </div>
                      {p.description && <p className="text-xs text-slate-600 mt-1">{p.description}</p>}
                      {p.resolution_note && <p className="text-xs text-emerald-700 mt-1">✔ {p.resolution_note}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {p.photo && <ClickablePhoto src={`${API}/files/${p.photo}?auth=${token}`} alt="Photo problème" className="h-10 w-10" testid={`probleme-photo-${p.id || ''}`} />}
                      {!resolu && (
                        <Button size="sm" variant="ghost" className="text-emerald-600 h-8 w-8 p-0" title="Marquer résolu"
                          onClick={() => { setResolveForm({ resolution_note: "", cout: "" }); setResolveOpen(p); }} data-testid={`probleme-resolve-${p.id}`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => remove(p.id)} data-testid={`probleme-del-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialog signaler */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="probleme-dialog">
          <DialogHeader><DialogTitle>Signaler un problème</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre *</Label><Input value={form.titre || ""} onChange={e => setForm({ ...form, titre: e.target.value })} placeholder="Ex: Bruit moteur, rayure portière…" data-testid="probleme-titre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Gravité</Label>
                <Select value={form.gravite} onValueChange={v => setForm({ ...form, gravite: v })}>
                  <SelectTrigger data-testid="probleme-gravite"><SelectValue /></SelectTrigger>
                  <SelectContent>{GRAVITES.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Km au compteur</Label><Input type="number" value={form.km} onChange={e => setForm({ ...form, km: e.target.value })} /></div>
            </div>
            <div><Label>Signalé par</Label><Input value={form.signale_par || ""} onChange={e => setForm({ ...form, signale_par: e.target.value })} placeholder="Nom du conducteur" data-testid="probleme-signale-par" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Détails du problème…" /></div>
            <div>
              <Label className="mb-1 block">Photo (optionnel)</Label>
              <PhotoOptional fileId={form.photo} onUploaded={id => setForm({ ...form, photo: id })} />
            </div>
            <Button className="w-full bg-red-600 hover:bg-red-700" onClick={submit} disabled={saving} data-testid="submit-probleme">
              <AlertTriangle className="w-4 h-4 mr-2" />{saving ? "…" : "Signaler le problème"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog résoudre */}
      <Dialog open={!!resolveOpen} onOpenChange={v => !v && setResolveOpen(null)}>
        <DialogContent className="max-w-md" data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Résoudre : {resolveOpen?.titre}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Note de résolution</Label><Textarea rows={2} value={resolveForm.resolution_note} onChange={e => setResolveForm({ ...resolveForm, resolution_note: e.target.value })} placeholder="Ex: Réparé au garage Dupont" /></div>
            <div><Label>Coût réparation (€)</Label><Input type="number" step="0.01" value={resolveForm.cout} onChange={e => setResolveForm({ ...resolveForm, cout: e.target.value })} data-testid="resolve-cout" /></div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={resolve} disabled={saving} data-testid="submit-resolve">
              <CheckCircle2 className="w-4 h-4 mr-2" />{saving ? "…" : "Marquer comme résolu"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// PhotoField optionnel (sans astérisque)
const PhotoOptional = ({ fileId, onUploaded }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      onUploaded(res.data.id);
      toast.success("Photo ajoutée");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };
  return (
    <label className="block cursor-pointer">
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="probleme-photo" />
      {fileId ? (
        <img src={`${API}/files/${fileId}?auth=${token}`} alt="problème" className="h-24 w-auto rounded-lg border object-cover" />
      ) : (
        <div className="w-full h-20 border border-dashed rounded-lg flex items-center justify-center text-sm text-slate-500 hover:bg-slate-50">
          <Camera className="w-4 h-4 mr-2" />{uploading ? "Envoi…" : "Ajouter une photo"}
        </div>
      )}
    </label>
  );
};

export default ProblemesVehicule;
